// MedicareClientDetailView — the Medicare Operations Floor for a
// single client. Matte black + gold cinematic surface over
// /hq/hq-floor.jpg, built from the CRAFT.md §7 set pieces (glass
// panels, Ledger-style rows, SampleDataTag) and the warm-dark palette
// already established in src/hq/theme.js, with the CRAFT-specified
// brand gold (#C9A227) as the accent instead of the HQ amber so this
// surface reads as "PHI vault", not "workday floor".
//
// Data sources (all real, all owner-scoped):
//   GET  /api/medicare/client-detail?client_id=  — client, policies,
//        medical profile, consents, SOA status, commissions,
//        lifetime_value. Every MBI / member_id field arrives masked.
//   GET  /api/medicare/score?client_id=          — disclosed score +
//        per-factor breakdown (api/_medicareScore.js).
//   POST /api/medicare/mbi-reveal                — full plaintext MBI,
//        one owner-gated click, audited server-side before it's
//        returned, shown transiently and never persisted to state
//        beyond the reveal window.
//   POST /api/coverage/find-doctors               — provider network
//        lookup by ZIP (no ZIP on file for a client yet, so this is
//        operator-entered).
//
// Sections with no real backing table yet (household grouping, AI
// Workforce roster, ARIA suggestion chips) render honestly labeled
// SAMPLE DATA content rather than fabricating numbers.

import React from 'react';
import { supabase } from '../lib/supabase';
import { readPersistedJwtDirect } from '../lib/authedFetch';

const GOLD       = '#C9A227';
const GOLD_DIM    = '#8a7220';
const GOLD_HOT    = '#f0d47a';
const BASE        = '#0b0b0b';
const PANEL       = 'rgba(20,19,17,0.86)';
const PANEL2      = 'rgba(26,26,26,0.72)';
const LINE        = 'rgba(201,162,39,0.20)';
const LINE_STRONG = 'rgba(201,162,39,0.42)';
const IVORY       = '#F2EDE3';
const MUTED        = '#8B8578';
const GREEN        = '#5BA36F';
const RED          = '#C4584A';

const MONO = "'JetBrains Mono', ui-monospace, 'SF Mono', Menlo, monospace";
const DISPLAY = "'Fraunces', Georgia, serif";
const BODY = "'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif";

const MEDICARE_LINES = ['medicare_advantage', 'medicare_supplement', 'medicare_pdp'];
const LINE_LABEL = {
  medicare_advantage:  'Medicare Advantage (Part C)',
  medicare_supplement: 'Medicare Supplement (Medigap)',
  medicare_pdp:        'Part D (Prescription Drug)',
};
const AEP_MATCHER = /medicare|advantage|supplement|part\s?d|medigap/i;

async function apiFetch(path, opts = {}) {
  const jwt = readPersistedJwtDirect();
  const res = await fetch(path, {
    ...opts,
    headers: { 'content-type': 'application/json', ...(jwt ? { Authorization: `Bearer ${jwt}` } : {}), ...(opts.headers || {}) },
  });
  const body = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(body?.error || `HTTP ${res.status}`);
  return body;
}

// AEP runs Oct 15 -> Dec 7 every year. Pure calendar fact, not a
// fabricated countdown.
function aepWindow(today = new Date()) {
  const y = today.getUTCFullYear();
  const start = new Date(Date.UTC(y, 9, 15));
  const end   = new Date(Date.UTC(y, 11, 7));
  if (today < start) return { phase: 'pre', days: Math.ceil((start - today) / 86400000) };
  if (today <= end)  return { phase: 'active', days: Math.ceil((end - today) / 86400000) };
  const nextStart = new Date(Date.UTC(y + 1, 9, 15));
  return { phase: 'post', days: Math.ceil((nextStart - today) / 86400000) };
}

function fmtMoney(n) {
  const v = Number(n) || 0;
  return '$' + v.toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 0 });
}

function Glass({ children, style, title, eyebrow, right }) {
  return (
    <div style={{
      position: 'relative', background: PANEL, border: `1px solid ${LINE}`, borderRadius: 12,
      backdropFilter: 'blur(10px)', padding: '16px 18px', ...style,
    }}>
      {(title || right) && (
        <div style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', marginBottom: 10, gap: 10, flexWrap: 'wrap' }}>
          <div>
            {eyebrow && <div style={{ fontFamily: MONO, fontSize: 10, letterSpacing: '0.14em', textTransform: 'uppercase', color: GOLD_DIM }}>{eyebrow}</div>}
            {title && <div style={{ fontFamily: DISPLAY, fontSize: 17, color: IVORY, marginTop: 2 }}>{title}</div>}
          </div>
          {right}
        </div>
      )}
      {children}
    </div>
  );
}

// CRAFT §7 SampleDataTag — border-straddling tab. Ground-matched so it
// never collides with a card header.
function SampleDataTag() {
  return (
    <span style={{
      position: 'absolute', top: 0, right: 14, transform: 'translateY(-50%)',
      background: BASE, border: `1px solid ${LINE_STRONG}`, borderRadius: 999,
      padding: '2px 8px', fontFamily: MONO, fontSize: 9, letterSpacing: '0.12em',
      color: GOLD_HOT, textTransform: 'uppercase',
    }}>
      Sample Data
    </span>
  );
}

function Chip({ children, tone = 'gold', small }) {
  const tones = {
    gold:  { fg: GOLD_HOT, bg: 'rgba(201,162,39,0.10)', bd: LINE_STRONG },
    green: { fg: GREEN,    bg: 'rgba(91,163,111,0.10)', bd: 'rgba(91,163,111,0.35)' },
    red:   { fg: RED,      bg: 'rgba(196,88,74,0.10)',  bd: 'rgba(196,88,74,0.35)' },
    muted: { fg: MUTED,    bg: 'rgba(139,133,120,0.10)', bd: 'rgba(139,133,120,0.30)' },
  };
  const t = tones[tone] || tones.gold;
  return (
    <span style={{
      display: 'inline-flex', alignItems: 'center', gap: 4, padding: small ? '2px 7px' : '4px 10px',
      borderRadius: 999, background: t.bg, border: `1px solid ${t.bd}`, color: t.fg,
      fontFamily: MONO, fontSize: small ? 10 : 11, whiteSpace: 'nowrap',
    }}>
      {children}
    </span>
  );
}

// ── Client picker (rendered when no clientId is supplied) ──────────
function ClientPicker({ currentUser, onPick }) {
  const [loading, setLoading] = React.useState(true);
  const [clients, setClients] = React.useState([]);
  const [q, setQ] = React.useState('');

  React.useEffect(() => {
    if (!currentUser?.id) { setLoading(false); return; }
    let cancelled = false;
    (async () => {
      try {
        const { data } = await supabase.from('health_policies')
          .select('client_id, client_name, plan_type, status')
          .eq('owner_id', currentUser.id)
          .eq('status', 'active')
          .limit(3000);
        if (cancelled) return;
        const byClient = new Map();
        for (const p of (data || [])) {
          if (!p.client_id || !AEP_MATCHER.test(p.plan_type || '')) continue;
          if (!byClient.has(p.client_id)) byClient.set(p.client_id, { id: p.client_id, name: p.client_name || 'Unnamed client' });
        }
        setClients(Array.from(byClient.values()).sort((a, b) => String(a.name).localeCompare(String(b.name))));
      } catch { if (!cancelled) setClients([]); }
      if (!cancelled) setLoading(false);
    })();
    return () => { cancelled = true; };
  }, [currentUser?.id]);

  const filtered = q.trim()
    ? clients.filter(c => c.name.toLowerCase().includes(q.trim().toLowerCase()))
    : clients;

  return (
    <div style={{ minHeight: '70vh', background: `linear-gradient(180deg, rgba(11,11,11,0.72), rgba(11,11,11,0.90)), url(/hq/hq-floor.jpg) center/cover`, padding: 28, fontFamily: BODY }}>
      <div style={{ maxWidth: 640, margin: '40px auto' }}>
        <div style={{ fontFamily: MONO, fontSize: 10, letterSpacing: '0.14em', textTransform: 'uppercase', color: GOLD_DIM, marginBottom: 6 }}>
          Six Figure Agent OS &middot; Medicare Operations Floor
        </div>
        <div style={{ fontFamily: DISPLAY, fontSize: 30, color: IVORY, marginBottom: 4 }}>Select a Medicare client</div>
        <div style={{ fontSize: 13, color: MUTED, marginBottom: 18 }}>
          Pick a client from your active Medicare book to open their operations floor.
        </div>
        <input
          value={q} onChange={e => setQ(e.target.value)} placeholder="Search clients…"
          style={{ width: '100%', padding: '10px 14px', borderRadius: 8, background: PANEL2, border: `1px solid ${LINE}`, color: IVORY, fontSize: 13, marginBottom: 14, outline: 'none' }}
        />
        <Glass>
          {loading ? (
            <div style={{ color: MUTED, fontSize: 12, padding: '10px 0' }}>Loading your Medicare book…</div>
          ) : filtered.length === 0 ? (
            <div style={{ color: MUTED, fontSize: 12, padding: '10px 0' }}>
              No active Medicare policies on file yet. Add a Medicare Advantage, Supplement, or Part D policy for a client to see them here.
            </div>
          ) : (
            <div style={{ maxHeight: 420, overflowY: 'auto' }}>
              {filtered.map(c => (
                <button key={c.id} onClick={() => onPick(c.id)}
                  style={{
                    width: '100%', textAlign: 'left', display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                    padding: '10px 8px', borderRadius: 8, border: 'none', background: 'transparent', color: IVORY,
                    fontSize: 13, cursor: 'pointer', borderBottom: `1px solid ${LINE}`,
                  }}
                  onMouseEnter={e => { e.currentTarget.style.background = 'rgba(201,162,39,0.06)'; }}
                  onMouseLeave={e => { e.currentTarget.style.background = 'transparent'; }}
                >
                  <span>{c.name}</span>
                  <span style={{ fontFamily: MONO, fontSize: 10, color: GOLD_DIM }}>OPEN &rarr;</span>
                </button>
              ))}
            </div>
          )}
        </Glass>
      </div>
    </div>
  );
}

// ── Disclosed score breakdown ───────────────────────────────────────
function ScoreBreakdown({ score, loading }) {
  const [open, setOpen] = React.useState(false);
  if (loading) return <div style={{ color: MUTED, fontSize: 12 }}>Scoring…</div>;
  if (!score) return <div style={{ color: MUTED, fontSize: 12 }}>Score unavailable.</div>;
  const tone = score.overall >= 80 ? GREEN : score.overall >= 50 ? GOLD_HOT : RED;
  return (
    <div>
      <div style={{ display: 'flex', alignItems: 'baseline', gap: 14 }}>
        <div style={{ fontFamily: DISPLAY, fontSize: 42, color: tone, lineHeight: 1 }}>
          {score.overall}<span style={{ fontSize: 18, color: MUTED }}>/100</span>
        </div>
        <button onClick={() => setOpen(o => !o)} style={{
          background: 'transparent', border: `1px solid ${LINE_STRONG}`, color: GOLD_HOT, borderRadius: 999,
          padding: '4px 10px', fontFamily: MONO, fontSize: 10, cursor: 'pointer', textTransform: 'uppercase', letterSpacing: '0.08em',
        }}>
          {open ? 'Hide breakdown' : 'How it’s calculated'}
        </button>
      </div>
      {open && (
        <div style={{ marginTop: 12 }}>
          {(score.factors || []).map((f, i) => {
            const pct = f.max > 0 ? Math.round((f.points / f.max) * 100) : 0;
            return (
              <div key={i} style={{ marginBottom: 9 }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 12, color: IVORY }}>
                  <span>{f.label}</span>
                  <span style={{ fontFamily: MONO, color: MUTED }}>{f.points}/{f.max}</span>
                </div>
                <div style={{ height: 4, background: 'rgba(255,255,255,0.06)', borderRadius: 999, marginTop: 3, overflow: 'hidden' }}>
                  <div style={{ height: '100%', width: `${pct}%`, background: pct >= 70 ? GREEN : pct >= 35 ? GOLD : RED }} />
                </div>
                <div style={{ fontSize: 10.5, color: MUTED, marginTop: 2 }}>{f.detail}</div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

export default function MedicareClientDetailView({ currentUser, clientId, showToast = () => {}, onGotoTab = () => {} }) {
  const [pickedClientId, setPickedClientId] = React.useState(null);
  const effectiveClientId = clientId || pickedClientId;

  const [loading, setLoading] = React.useState(true);
  const [detail, setDetail] = React.useState(null);
  const [score, setScore] = React.useState(null);
  const [scoreLoading, setScoreLoading] = React.useState(true);
  const [error, setError] = React.useState(null);

  const [revealedMbi, setRevealedMbi] = React.useState(null);
  const [revealing, setRevealing] = React.useState(false);
  const revealTimer = React.useRef(null);

  const [vipBusy, setVipBusy] = React.useState(false);

  const [zip, setZip] = React.useState('');
  const [pnLoading, setPnLoading] = React.useState(false);
  const [pnResult, setPnResult] = React.useState(null);
  const [pnError, setPnError] = React.useState(null);

  const load = React.useCallback(async () => {
    if (!effectiveClientId) return;
    setLoading(true); setScoreLoading(true); setError(null);
    try {
      const d = await apiFetch(`/api/medicare/client-detail?client_id=${encodeURIComponent(effectiveClientId)}`);
      setDetail(d);
    } catch (e) {
      setError(e.message || 'Failed to load client');
    }
    setLoading(false);
    try {
      const s = await apiFetch(`/api/medicare/score?client_id=${encodeURIComponent(effectiveClientId)}`);
      setScore(s);
    } catch { setScore(null); }
    setScoreLoading(false);
  }, [effectiveClientId]);

  React.useEffect(() => { load(); }, [load]);
  React.useEffect(() => () => { if (revealTimer.current) clearTimeout(revealTimer.current); }, []);

  const canReveal = currentUser?.role !== 'va'; // owner-only: hide the reveal action from delegated VA seats

  async function handleReveal() {
    if (!effectiveClientId || revealing) return;
    setRevealing(true);
    try {
      const r = await apiFetch('/api/medicare/mbi-reveal', {
        method: 'POST',
        body: JSON.stringify({ client_id: effectiveClientId, reason: 'operator_view' }),
      });
      setRevealedMbi(r.mbi || null);
      if (revealTimer.current) clearTimeout(revealTimer.current);
      // Transient only — auto-clear from state after 20s so a full
      // plaintext MBI never lingers in memory/DOM longer than needed.
      revealTimer.current = setTimeout(() => setRevealedMbi(null), 20000);
    } catch (e) {
      showToast(e.message === 'mbi_unavailable' || e.message === 'mbi_decrypt_failed'
        ? 'MBI is not available to decrypt (encryption key or ciphertext missing).'
        : (e.message || 'Reveal failed'));
    }
    setRevealing(false);
  }

  async function toggleVip() {
    if (!effectiveClientId || vipBusy) return;
    setVipBusy(true);
    try {
      const d = await apiFetch(`/api/medicare/client-detail?client_id=${encodeURIComponent(effectiveClientId)}`, {
        method: 'PATCH',
        body: JSON.stringify({ is_vip: !detail?.is_vip }),
      });
      setDetail(d);
    } catch (e) {
      showToast(e.message || 'Update failed');
    }
    setVipBusy(false);
  }

  async function runProviderSearch() {
    if (!/^\d{5}$/.test(zip)) { setPnError('Enter a 5-digit ZIP.'); return; }
    setPnLoading(true); setPnError(null); setPnResult(null);
    try {
      const r = await apiFetch('/api/coverage/find-doctors', {
        method: 'POST',
        body: JSON.stringify({ zip, state: detail?.state_abbr || '' }),
      });
      setPnResult(r);
    } catch (e) {
      setPnError(e.message || 'Provider search failed');
    }
    setPnLoading(false);
  }

  if (!effectiveClientId) {
    return <ClientPicker currentUser={currentUser} onPick={setPickedClientId} />;
  }

  const aep = aepWindow();
  const policies = detail?.policies || [];
  const medicarePolicies = policies.filter(p => MEDICARE_LINES.includes(p.plan_type));
  const ancillaryPolicies = policies.filter(p => !MEDICARE_LINES.includes(p.plan_type));
  const soaOnFile = !!detail?.soa;
  const signedConsent = (detail?.consents || []).some(c => c.status === 'signed');
  const mbiEncrypted = !!(detail?.mbi_masked);
  const commissionLinked = (detail?.lifetime_value || 0) > 0;

  const sentinelChecks = [
    { label: 'SOA on file (CMS plan-discussion gate)', pass: soaOnFile },
    { label: 'Consent signed', pass: signedConsent },
    { label: 'MBI stored encrypted', pass: mbiEncrypted },
    { label: 'Commission linked to a policy', pass: commissionLinked },
  ];

  return (
    <div style={{ minHeight: '100vh', background: `linear-gradient(180deg, rgba(11,11,11,0.80), rgba(11,11,11,0.94)), url(/hq/hq-floor.jpg) center/cover fixed`, fontFamily: BODY, padding: '24px 22px 60px' }}>
      <div style={{ maxWidth: 1240, margin: '0 auto' }}>

        {/* Mission Control bar */}
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 14, justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 18 }}>
          <div style={{ minWidth: 0 }}>
            <div style={{ fontFamily: MONO, fontSize: 10, letterSpacing: '0.14em', textTransform: 'uppercase', color: GOLD_DIM }}>
              Six Figure Agent OS &middot; Medicare Operations Floor
            </div>
            <div style={{ fontFamily: DISPLAY, fontSize: 30, color: IVORY, marginTop: 2 }}>
              {loading ? 'Loading…' : (detail?.name || 'Client')}
            </div>
            <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginTop: 8 }}>
              {detail?.is_vip && <Chip tone="gold">VIP</Chip>}
              <Chip tone={soaOnFile ? 'green' : 'red'} small>{soaOnFile ? 'SOA on file' : 'No SOA'}</Chip>
              <Chip tone="muted" small>{medicarePolicies.length} Medicare polic{medicarePolicies.length === 1 ? 'y' : 'ies'}</Chip>
              {clientId ? null : (
                <button onClick={() => setPickedClientId(null)} style={{ background: 'transparent', border: 'none', color: GOLD_DIM, fontFamily: MONO, fontSize: 10, cursor: 'pointer', textDecoration: 'underline' }}>
                  change client
                </button>
              )}
            </div>
          </div>
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
            <button onClick={toggleVip} disabled={vipBusy || loading} style={{
              background: 'transparent', border: `1px solid ${LINE_STRONG}`, color: GOLD_HOT, borderRadius: 8,
              padding: '8px 12px', fontFamily: MONO, fontSize: 11, cursor: 'pointer', opacity: vipBusy ? 0.5 : 1,
            }}>
              {detail?.is_vip ? 'Remove VIP' : 'Mark VIP'}
            </button>
            <button onClick={() => onGotoTab('medicare_command')} style={{
              background: GOLD, border: 'none', color: BASE, borderRadius: 8, padding: '8px 14px',
              fontFamily: BODY, fontWeight: 700, fontSize: 11.5, cursor: 'pointer',
            }}>
              Back to Command Center
            </button>
          </div>
        </div>

        {error && (
          <div style={{ background: 'rgba(196,88,74,0.10)', border: `1px solid rgba(196,88,74,0.35)`, color: '#f4b4b6', padding: '10px 14px', borderRadius: 8, fontSize: 12.5, marginBottom: 16 }}>
            {error}
          </div>
        )}

        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))', gap: 14, marginBottom: 14 }}>

          {/* MBI vault */}
          <Glass eyebrow="PHI vault" title="Medicare Beneficiary Identifier">
            <div style={{ fontFamily: MONO, fontSize: 20, color: revealedMbi ? GOLD_HOT : IVORY, letterSpacing: '0.06em', minWidth: 0, overflowWrap: 'anywhere' }}>
              {revealedMbi || detail?.mbi_masked || '••••'}
            </div>
            {revealedMbi && <div style={{ fontSize: 10.5, color: GOLD_HOT, marginTop: 4 }}>Revealed &mdash; clears automatically in this view.</div>}
            {canReveal ? (
              <button onClick={handleReveal} disabled={revealing || loading} style={{
                marginTop: 10, background: 'transparent', border: `1px solid ${LINE_STRONG}`, color: GOLD_HOT,
                borderRadius: 8, padding: '7px 12px', fontFamily: MONO, fontSize: 10.5, cursor: 'pointer', opacity: revealing ? 0.5 : 1,
              }}>
                {revealing ? 'Revealing…' : (revealedMbi ? 'Reveal again' : 'Reveal')}
              </button>
            ) : (
              <div style={{ fontSize: 10.5, color: MUTED, marginTop: 8 }}>Reveal is restricted to the account owner.</div>
            )}
            <div style={{ fontSize: 10, color: MUTED, marginTop: 8 }}>Every reveal is written to the immutable audit trail before it is shown.</div>
          </Glass>

          {/* Revenue */}
          <Glass eyebrow="Revenue" title="Lifetime commission">
            <div style={{ fontFamily: DISPLAY, fontSize: 34, color: GOLD_HOT }}>
              {loading ? '…' : fmtMoney(detail?.lifetime_value)}
            </div>
            <div style={{ fontSize: 11.5, color: MUTED, marginTop: 4 }}>Sum of commission rows linked to this client&rsquo;s policies.</div>
          </Glass>

          {/* AEP dashboard */}
          <Glass eyebrow="Annual Enrollment Period" title={aep.phase === 'active' ? 'AEP is open' : 'AEP countdown'}>
            <div style={{ fontFamily: DISPLAY, fontSize: 34, color: IVORY }}>{aep.days}<span style={{ fontSize: 15, color: MUTED }}> days</span></div>
            <div style={{ fontSize: 11.5, color: MUTED, marginTop: 4 }}>
              {aep.phase === 'active' ? 'remaining in this year’s Oct 15 – Dec 7 window.'
                : aep.phase === 'pre' ? 'until AEP opens (Oct 15).'
                : 'until next year’s AEP opens.'}
            </div>
          </Glass>
        </div>

        {/* Coverage cards */}
        <Glass eyebrow="Coverage" title="Medicare coverage on file" style={{ marginBottom: 14 }}>
          {medicarePolicies.length === 0 ? (
            <div style={{ color: MUTED, fontSize: 12.5 }}>No Medicare Advantage, Supplement, or Part D policy on file for this client.</div>
          ) : (
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))', gap: 10 }}>
              {medicarePolicies.map((p, i) => (
                <div key={i} style={{ border: `1px solid ${LINE}`, borderRadius: 10, padding: 12, background: PANEL2, minWidth: 0 }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', gap: 8, alignItems: 'flex-start' }}>
                    <span style={{
                      fontFamily: MONO, fontSize: 10.5, padding: '3px 8px', borderRadius: 6,
                      border: `1px solid ${LINE_STRONG}`, color: GOLD_HOT, whiteSpace: 'nowrap',
                    }} title="Stylized placeholder — official carrier logo assets replace these badges.">
                      {(p.carrier || 'CARRIER').toUpperCase()}
                    </span>
                    <Chip tone={p.status === 'active' ? 'green' : p.status === 'pending' ? 'gold' : 'red'} small>{p.status}</Chip>
                  </div>
                  <div style={{ fontSize: 13, color: IVORY, marginTop: 8 }}>{LINE_LABEL[p.plan_type] || p.plan_type}</div>
                  <div style={{ fontSize: 11.5, color: MUTED, marginTop: 2 }}>{p.plan_name || 'Plan name not on file'}</div>
                  <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: 8, fontFamily: MONO, fontSize: 11 }}>
                    <span style={{ color: MUTED }}>Member ID {p.member_id_masked || '—'}</span>
                    <span style={{ color: GOLD_DIM }}>{p.monthly_premium != null ? `${fmtMoney(p.monthly_premium)}/mo` : ''}</span>
                  </div>
                </div>
              ))}
            </div>
          )}
        </Glass>

        {/* Ancillary row */}
        <Glass eyebrow="Ancillary" title="Other coverage on file" style={{ marginBottom: 14 }}>
          {ancillaryPolicies.length === 0 ? (
            <div style={{ color: MUTED, fontSize: 12.5 }}>No dental, vision, hospital indemnity, or other ancillary coverage on file.</div>
          ) : (
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
              {ancillaryPolicies.map((p, i) => (
                <Chip key={i} tone={p.status === 'active' ? 'green' : 'muted'}>{p.carrier} &middot; {p.plan_type}</Chip>
              ))}
            </div>
          )}
        </Glass>

        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(360px, 1fr))', gap: 14, marginBottom: 14 }}>

          {/* Prescription Intelligence */}
          <Glass eyebrow="Prescription Intelligence" title="Current prescriptions on file">
            <div style={{ fontSize: 10.5, color: MUTED, marginBottom: 8 }}>
              Plan and formulary information only. This is not medical advice.
            </div>
            {(detail?.medical?.current_prescriptions || []).length === 0 ? (
              <div style={{ color: MUTED, fontSize: 12.5 }}>No prescriptions on file for this client.</div>
            ) : (
              <ul style={{ margin: 0, paddingLeft: 18, fontSize: 12.5, color: IVORY }}>
                {detail.medical.current_prescriptions.map((rx, i) => (
                  <li key={i} style={{ marginBottom: 4 }}>{typeof rx === 'string' ? rx : (rx?.name || 'Prescription on file')}</li>
                ))}
              </ul>
            )}
          </Glass>

          {/* Provider Network */}
          <Glass eyebrow="Provider Network" title="Find providers near this client">
            <div style={{ fontSize: 10.5, color: MUTED, marginBottom: 8 }}>
              {detail?.medical?.provider_network_verified_at
                ? `Network last verified ${new Date(detail.medical.provider_network_verified_at).toLocaleDateString()}.`
                : 'No provider-network verification on file for this client yet.'}
            </div>
            <div style={{ display: 'flex', gap: 8 }}>
              <input value={zip} onChange={e => setZip(e.target.value.replace(/\D/g, '').slice(0, 5))} placeholder="ZIP code"
                style={{ flex: 1, minWidth: 0, padding: '8px 10px', borderRadius: 8, background: PANEL2, border: `1px solid ${LINE}`, color: IVORY, fontFamily: MONO, fontSize: 12 }}/>
              <button onClick={runProviderSearch} disabled={pnLoading} style={{
                background: GOLD, border: 'none', color: BASE, borderRadius: 8, padding: '8px 14px', fontWeight: 700, fontSize: 11.5, cursor: 'pointer',
              }}>
                {pnLoading ? 'Searching…' : 'Search'}
              </button>
            </div>
            {pnError && <div style={{ color: '#f4b4b6', fontSize: 11.5, marginTop: 8 }}>{pnError}</div>}
            {pnResult && (
              <div style={{ marginTop: 10, maxHeight: 220, overflowY: 'auto' }}>
                {pnResult.categories.filter(c => c.results.length > 0).length === 0 && (
                  <div style={{ color: MUTED, fontSize: 12 }}>No providers found within {pnResult.radius_miles} miles of {pnResult.zip}.</div>
                )}
                {pnResult.categories.filter(c => c.results.length > 0).map(c => (
                  <div key={c.id} style={{ marginBottom: 8 }}>
                    <div style={{ fontFamily: MONO, fontSize: 10, color: GOLD_DIM, textTransform: 'uppercase' }}>{c.label}</div>
                    {c.results.slice(0, 3).map((r, i) => (
                      <div key={i} style={{ fontSize: 12, color: IVORY }}>{r.name}{r.distance_mi != null ? ` · ${r.distance_mi} mi` : ''}</div>
                    ))}
                  </div>
                ))}
                <div style={{ fontSize: 10, color: MUTED, marginTop: 4 }}>Best-effort public directory data &mdash; confirm in-network status with the carrier before enrollment.</div>
              </div>
            )}
          </Glass>
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(360px, 1fr))', gap: 14, marginBottom: 14 }}>

          {/* Sentinel compliance monitor */}
          <Glass eyebrow="SENTINEL" title="Compliance monitor">
            {sentinelChecks.map((c, i) => (
              <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '6px 0', borderBottom: i < sentinelChecks.length - 1 ? `1px solid ${LINE}` : 'none' }}>
                <span style={{
                  width: 8, height: 8, borderRadius: 999, background: c.pass ? GREEN : RED, flexShrink: 0,
                }}/>
                <span style={{ fontSize: 12.5, color: IVORY }}>{c.label}</span>
              </div>
            ))}
            <div style={{ fontSize: 10.5, color: MUTED, marginTop: 10, lineHeight: 1.5 }}>
              SENTINEL continuously monitors required workflow steps on this client&rsquo;s file. It does not guarantee regulatory compliance &mdash; use it as a checklist, not a legal opinion.
            </div>
          </Glass>

          {/* Disclosed Medicare Score */}
          <Glass eyebrow="Medicare Score" title="Disclosed score">
            <ScoreBreakdown score={score} loading={scoreLoading} />
          </Glass>
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(360px, 1fr))', gap: 14, marginBottom: 14 }}>

          {/* Household / Opportunity */}
          <Glass eyebrow="Household" title="Household &amp; opportunity" style={{ position: 'relative' }}>
            <SampleDataTag/>
            <div style={{ color: MUTED, fontSize: 12.5, marginBottom: 8 }}>
              Household grouping has no dedicated table yet &mdash; this section shows the shape of the feature with sample rows.
            </div>
            <div style={{ fontSize: 12.5, color: IVORY }}>Jane Beneficiary <span style={{ color: MUTED }}>(primary, this client)</span></div>
            <div style={{ fontSize: 12.5, color: MUTED }}>Spouse / dependents not linked yet</div>
          </Glass>

          {/* AI Workforce row */}
          <Glass eyebrow="AI Workforce" title="Assigned to this client" style={{ position: 'relative' }}>
            {detail?.assigned_ai_agent_id ? (
              <Chip tone="gold">Agent {String(detail.assigned_ai_agent_id).slice(0, 8)} assigned</Chip>
            ) : (
              <>
                <SampleDataTag/>
                <div style={{ color: MUTED, fontSize: 12.5, marginBottom: 8 }}>
                  No AI employee assigned yet. Illustrative roster:
                </div>
                <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                  <Chip tone="muted" small>SENTINEL &middot; compliance</Chip>
                  <Chip tone="muted" small>NOVA &middot; nurture</Chip>
                  <Chip tone="muted" small>ENROLL &middot; AEP outreach</Chip>
                </div>
              </>
            )}
          </Glass>
        </div>

        {/* ARIA assistant panel */}
        <Glass eyebrow="ARIA" title="Ask about this client" style={{ position: 'relative' }}>
          <SampleDataTag/>
          <div style={{ color: MUTED, fontSize: 12.5, marginBottom: 10 }}>
            ARIA is not wired to this view yet. Illustrative prompts an operator could ask once connected:
          </div>
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
            {['What changed on this client’s file this month?', 'Is this client ready for AEP outreach?', 'Summarize the compliance gaps.'].map((s, i) => (
              <span key={i} style={{
                fontSize: 11.5, color: MUTED, border: `1px dashed ${LINE}`, borderRadius: 999, padding: '5px 10px',
              }}>{s}</span>
            ))}
          </div>
        </Glass>

      </div>
    </div>
  );
}
